<template>
    <LoaderComponent v-if="loading" />

    <div>
        <ShipmentHeader />

        <div class="mt-4 space-y-6">
            <template v-if="packages.data && packages.data.length > 0">
                <div v-for="(pkg) in packages.data" :key="pkg.id"
                    class="bg-white rounded-2xl shadow-sm border border-gray-500 hover:shadow-md transition-all duration-200">
                    <!-- Card Header -->
                    <div class="flex items-center justify-between bg-[#f6e2b5] px-6 py-3 rounded-t-2xl relative">
                        <!-- Left Section (Package Info) -->
                        <div>
                            <h2 class="text-md sm:text-lg font-semibold font-poppins text-gray-800">
                                Package ID: {{ pkg.id }}
                            </h2>
                            <p class="text-xs sm:text-sm text-gray-600 mt-1">Date: {{ pkg.date || '2025-11-03' }}</p>
                        </div>

                        <!-- Right Section (Status + Menu) -->
                        <div class="flex items-center gap-3 ">
                            <span
                                class="text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-800 shadow-sm inline-flex items-center gap-1">
                                <i v-if="pkg.package_status_id == 2" class="fas fa-pencil-alt"></i>
                                <i v-else class="fas fa-hourglass-half"></i>
                                {{ pkg.package_status_id == 2 ? "Draft" : "Pending" }}
                            </span>


                            <!-- Dropdown Button -->
                            <button :id="`dropdownMenuIconButton-${pkg.id}`" @click="toggleDropdown(pkg.id)"
                                class="p-2 rounded-lg hover:bg-gray-100 focus:ring-2 focus:ring-gray-200 bg-white"
                                type="button">
                                <svg class="w-3 h-3 sm:w-5 sm:h-5 text-gray-700" aria-hidden="true"
                                    xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 4 15">
                                    <path
                                        d="M3.5 1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm0 6.041a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm0 5.959a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
                                </svg>
                            </button>

                            <!-- Dropdown -->
                            <div v-if="openDropdownId === pkg.id"
                                class="absolute mt-32 right-8 z-20 bg-white divide-y divide-gray-100 rounded-lg shadow w-44">
                                <div class="py-2">
                                    <a href="#" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        @click="editPackage(pkg.id)">
                                        Edit
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>


                    <!-- Card Body -->
                    <div class="p-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <!-- Package Info -->
                        <div>
                            <div class="bg-gray-200 px-3 py-2 rounded-md mb-3">
                                <h3 class="text-sm sm:text-base font-semibold font-inter text-gray-800">Package Info
                                </h3>
                            </div>
                            <div class="text-sm">
                                <p><b>Service:</b> {{ pkg.service_label }}</p>
                                <p><b>Box:</b> {{ pkg.package_type_code }}</p>
                                <p><b>Total Packages:</b> {{ pkg.boxes.length }}</p>
                                <p>
                                    <b>Total Weight:</b>
                                    {{pkg.boxes.reduce((total, box) => total + box.weight, 0)}} lb
                                </p>
                            </div>
                        </div>

                        <!-- Sender Info -->
                        <div v-if="pkg?.ship_from">
                            <div class="bg-gray-200 px-3 py-2 rounded-md mb-3">
                                <h3 class="text-sm sm:text-base font-semibold font-inter text-gray-800">Sender</h3>
                            </div>
                            <div class="text-sm">
                                <p><b>Name:</b> {{ pkg.ship_from.fullname }}</p>
                                <p><b>Address:</b> {{ pkg.ship_from.address }}</p>
                                <p v-if="pkg.ship_from.address_2">
                                    <b>Address 2:</b> {{ pkg.ship_from.address_2 }}
                                </p>
                                <p v-if="pkg.ship_from.address_3">
                                    <b>Address 3:</b> {{ pkg.ship_from.address_3 }}
                                </p>
                                <p><b>Zip:</b> {{ pkg.ship_from.zip_code }}</p>
                                <p><b>City:</b> {{ pkg.ship_from.city }}</p>
                                <p><b>Country:</b> {{ pkg.ship_from.country?.name }}</p>
                            </div>
                        </div>

                        <!-- Receiver Info -->
                        <div v-if="pkg?.ship_to">
                            <div class="bg-gray-200 px-3 py-2 rounded-md mb-3">
                                <h3 class="text-sm sm:text-base font-semibold text-gray-800">Receiver</h3>
                            </div>
                            <div class="text-sm">
                                <p><b>Name:</b> {{ pkg.ship_to.fullname }}</p>
                                <p><b>Address:</b> {{ pkg.ship_to.address }}</p>
                                <p v-if="pkg.ship_to.address_2">
                                    <b>Address 2:</b> {{ pkg.ship_to.address_2 }}
                                </p>
                                <p v-if="pkg.ship_to.address_3">
                                    <b>Address 3:</b> {{ pkg.ship_to.address_3 }}
                                </p>
                                <p><b>Zip:</b> {{ pkg.ship_to.zip_code }}</p>
                                <p><b>City:</b> {{ pkg.ship_to.city }}</p>
                                <p><b>Country:</b> {{ pkg.ship_to.country?.name }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Empty State -->
            <div v-else class="flex flex-col items-center justify-center text-center p-6 mt-10"
                style="margin-bottom: 150px;">
                <h1 class="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
                    There are no partial packages.
                </h1>
                <img class="h-[65px] sm:h-[72px] w-[100px] sm:w-[120px] sm:object-contain mt-2" src="@/assets/logo.svg"
                    alt="No Orders" />
            </div>
        </div>

        <Pagination v-if="packages.data && packages.data.length > 0" :pagination="packages" @fetchRecords="fetchRecords"
            class="mt-6" />
    </div>
</template>


<script>
import Pagination from "@/components/Global/Pagination.vue";
import LoaderComponent from "@/components/Partials/LoaderComponent.vue";
import ShipmentHeader from '@/components/Account/Shipment/ShipmentHeader.vue';

export default {
    name: "OrderPartial",
    data() {
        return {
            packages: [],
            loading: false,
            project_url: process.env.VUE_APP_PROJECT_URL,
            form: {
                package_id: "",
            },
            openDropdownId: null,
        };
    },
    components: { Pagination, LoaderComponent, ShipmentHeader },

    methods: {
        fetchRecords() {
            this.loading = true;
            const url = "package/index";

            var filters = {
                package_status_id: [1, 2, 3]
            }

            this.$axios
                .post(url, filters, {
                    headers: this.$headers,
                })
                .then((response) => {
                    this.packages = response.data.data.packages;
                    console.log(response);
                    this.loading = false;
                })
                .catch((error) => {
                    console.log(error);
                    this.loading = false;
                });
        },
        editPackage(package_id) {
            this.errors = {};
            this.loading = true;
            this.form.package_id = package_id;
            this.$axios
                .post("package/edit-package", this.form, {
                    headers: this.$headers
                })
                .then((response) => {
                    if (response.data.data.package_id) {
                        this.$router.push({ name: "account.shipments.edit", params: { id: response.data.data.package_id } })
                    } else {
                        this.$router.push({ name: "account.shipments" })
                    }
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        toggleDropdown(id) {
            this.openDropdownId = this.openDropdownId === id ? null : id;
        },
    },
    mounted() {
        this.fetchRecords();
    }
};
</script>